import { alpha, ButtonBase, Stack, Typography } from "@mui/material";
import { CurrencyBadge } from "Components/Dashboard/BalanceWithPaymentActions/MobileBalance/Components/CurrencyBadge";
import type { CurrencyBalance } from "Components/Dashboard/BalanceWithPaymentActions/types";
import { ResponsiveDialog } from "Components/Shared/Dialogs/ResponsiveDialog";
import { Resources, useResource } from "Translations/Resources";
import { formatCurrency, getCurrencyDisplayName } from "Utils/CurrencyUtils";
import { useTranslation } from "react-i18next";

const RESOURCES = Resources.Balance.BalanceContent;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  balances: ReadonlyArray<CurrencyBalance>;
  selectedCurrency?: string;
  isBalanceBlurred: boolean;
  onSelect: (currency: string) => void;
};

/**
 * "My currencies": every currency the persona holds with its balance. Tapping a row makes it the
 * one the dashboard shows and closes the sheet. Amounts honour the eye toggle like the hero does.
 */
export const CurrencyBalancesSheet = ({
  isOpen,
  onClose,
  balances,
  selectedCurrency,
  isBalanceBlurred,
  onSelect,
}: Props) => {
  const { t } = useResource();
  // i18n.language is reactive; useLanguage keeps its own copy that only syncs on mount.
  const { i18n } = useTranslation();

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={onClose}
      title={t(RESOURCES.CurrencySheetTitle)}
    >
      <Stack gap={1}>
        {balances.map(({ currency, total }) => {
          if (!currency) {
            return null;
          }
          const isSelected = currency === selectedCurrency;

          return (
            <ButtonBase
              key={currency}
              onClick={() => {
                onSelect(currency);
                onClose();
              }}
              aria-pressed={isSelected}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 3,
                width: "100%",
                px: 3,
                py: 2.5,
                borderRadius: 4,
                textAlign: "left",
                bgcolor: isSelected
                  ? (theme) => alpha(theme.palette.primary.main, 0.08)
                  : "transparent",
              }}
            >
              <CurrencyBadge
                currency={currency}
                size={44}
                colorVariant="dark"
              />
              <Stack flex={1} minWidth={0}>
                <Typography fontWeight={700}>{currency}</Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {getCurrencyDisplayName(currency, i18n.language)}
                </Typography>
              </Stack>
              <Typography
                fontWeight={700}
                sx={{
                  filter: isBalanceBlurred ? "blur(6px)" : "none",
                  userSelect: isBalanceBlurred ? "none" : "auto",
                }}
              >
                {formatCurrency({
                  amount: total,
                  currencyCode: currency,
                  minDecimalPlaces: 2,
                  maxDecimalPlaces: 2,
                })}
              </Typography>
            </ButtonBase>
          );
        })}
      </Stack>
    </ResponsiveDialog>
  );
};
